import React, { useEffect, useMemo, useState } from 'react';
import { Route, Routes, useParams } from 'react-router-dom';
import { useHistory } from '@useHistory/useHistoryCompat';
import { Activation } from '../models/activation';
import { ActivationFactory } from '../utils';
import { EntityUIState } from '../../../core/interfaces/entitiy-ui-state';
import { HevoEntity } from '../../../core/interfaces/hevo-entity';
import { RecentEntitiesService } from '../../../core/services/recent-entities.service';
import { getEntityErrorMessage } from '../../../core/utils/entity';
import { EntityErrorPane } from '../../../banner/components/entity-error-pane/EntityErrorPane';
import { HdCoordinatedHeightAnchor } from '../../../layout/components/coordinated-height/HdCoordinatedHeight';
import { HdCoordinatedScroll } from '../../../a11y/scroll/components/coordinated-scroll/HdCoordinatedScroll';
import RetryApiAlert from '../../../fallback-screens/components/retry-api-alert/RetryApiAlert';
import ScrollToTopButton from '../../../a11y/scroll/components/scroll-to-top-button/ScrollToTopButton';
import { SecondarySidebar } from '../../../layout/components/secondary-sidebar/SecondarySidebar';
import { TabProps } from '../../../layout/interfaces/secondarySidebar';
import { GenericPageShimmer } from '../../../loader/components/generic-page-shimmer/GenericPageShimmer';
import { useQuery } from '../../../core/hooks/useQuery';
import { useRedirectOnResolveError } from '../../../routing/hooks/useRedirectOnResolveError';
import useService from '../../../injector/hooks/useService';
import { getDataIdGenerator } from '../../../core/utils/generateDataId';
import { ActivityLogPageWrapper } from '../../activity-log/ActivityLogPage';
import { ActivationHeader } from '../ActivationHeader';
import { ActivationOverview } from '../ActivationOverview';
import ActivationsAPI from '../api/ActivationsAPI';
import { ActivationSyncHistory } from '../ActivationSyncHistory';

export function ShowActivation() {
  const { seqId } = useParams<{ seqId: string }>();

  const activationSeqId = parseInt(seqId, 10);

  const history = useHistory();

  const {
    data: activation,
    uiState,
    error,
    fetch: getActivation,
    writeData,
    refetch: refetchActivation
  } = useQuery<Activation>({
    dataSource$: () =>
      ActivationsAPI.getActivation(activationSeqId).then(res => {
        return ActivationFactory(res.data);
      }),
    fetchSuccess: _activation => {
      if (_activation.deleted) {
        history.replace('/activation');
      }
    },
    pollInterval: 60000
  });

  useRedirectOnResolveError({
    error,
    redirectPath: '/activation',
    entityLabel: 'Activation',
    entityId: activationSeqId
  });

  useEffect(() => {
    getActivation();
  }, [activationSeqId]);

  useEffect(() => {
    if (activation?.deleted) {
      history.replace('/activation');
    }
  }, [activation]);

  const dataIdGenerator = getDataIdGenerator('activationShow');

  return (
    <>
      {uiState === EntityUIState.LOADING ? <GenericPageShimmer /> : null}

      {uiState === EntityUIState.IDLE || (uiState === EntityUIState.REFRESHING && activation) ? (
        <ShowActivationInner
          activation={activation}
          setActivation={a => {
            writeData(() => a);
          }}
          onActivationEdit={refetchActivation}
          dataIdGenerator={dataIdGenerator}
        />
      ) : null}

      {uiState === EntityUIState.ERRORED ? (
        <RetryApiAlert
          error={error}
          actionHandler={() => getActivation()}
          dataId={dataIdGenerator('')}
        />
      ) : null}
    </>
  );
}

function ShowActivationInner({
  activation,
  setActivation,
  onActivationEdit,
  dataIdGenerator
}: {
  activation: Activation;
  setActivation: (activation: Activation) => any;
  onActivationEdit: Function;
  dataIdGenerator: Function;
}) {
  const [coordinatedScroll, setCoordinatedScroll] = useState(null);
  const [withScrollTopButton, setWithScrollTopButton] = useState(true);

  const errorMessage = activation.failureMsg;
  const history = useHistory();

  const tabs = useMemo<TabProps[]>(() => {
    return [
      {
        label: 'Overview',
        route: '/overview',
        path: `./overview`,
        Component: ActivationOverview,
        shortcut: 'activation.overview',
        icon: 'query-history',
        props: {
          activation,
          setActivation,
          setWithScrollTopButton
        }
      },
      {
        label: 'Sync History',
        route: '/history',
        path: `./history`,
        Component: ActivationSyncHistory,
        icon: 'history',
        shortcut: 'activation.history',
        props: {
          activation
        }
      },
      {
        label: 'Activity Log',
        route: '/activity-log',
        path: `./activity-log`,
        icon: 'activity-log',
        shortcut: 'activation.activityLog',
        Component: ActivityLogPageWrapper,
        props: {
          entityFilters: {
            entities_l0: [
              {
                type: 'ACTIVATION',
                id: activation.seqId,
                entities_l1: []
              }
            ]
          }
        }
      }
    ];
  }, [activation]);

  useEffect(() => {
    if (history.location.pathname.split('?')[0] === `/activation/${activation.seqId}`) {
      history.replace(`/activation/${activation.seqId}/overview`);
    }
  }, [activation]);

  return (
    <div className='d-flex h-100'>
      <SecondarySidebar tabs={tabs} />

      <div className='main-content-wrapper flex-1'>
        <HdCoordinatedScroll elRef={coordinatedScroll}>
          <HdCoordinatedHeightAnchor
            Component='div'
            id={dataIdGenerator('')}
            className='main-content-area scrollable-content flex-1'
            heightOffset={40}
            ref={el => {
              setCoordinatedScroll(el);
            }}
          >
            <div className='floating-header-container'>
              <ActivationHeader activation={activation} onActivationEdit={onActivationEdit} />

              {!!errorMessage ? (
                <EntityErrorPane
                  dataId={dataIdGenerator('')}
                  className='mt-5'
                  reason={errorMessage}
                  entity={HevoEntity.ACTIVATION}
                  entitySeqId={activation.seqId}
                  getSupportMessage={getEntityErrorMessage}
                />
              ) : null}
            </div>

            <Routes>
              {tabs.map(tab => {
                const TabComponent = tab.Component;
                return (
                  <Route
                    key={tab.label}
                    path={tab.route}
                    element={<TabComponent {...tab.props} />}
                  />
                );
              })}
            </Routes>
          </HdCoordinatedHeightAnchor>

          {withScrollTopButton ? (
            <ScrollToTopButton scrollViewportId={dataIdGenerator('')} />
          ) : null}
        </HdCoordinatedScroll>
      </div>
    </div>
  );
}
